import { useRef, useState } from 'react';
import { ActionIcon, Box, Group, UnstyledButton } from '@mantine/core';
import { IconCamera, IconPhoto, IconTrash } from '@tabler/icons-react';
import { Avatar } from '../../atoms/Avatar';
import { Text } from '../../atoms/Text';
import { Stack } from '../../atoms/Stack';
import { Button } from '../../atoms/Button';
import { BottomDrawer } from '../BottomDrawer';
import { useShouldRender } from '../../../hooks/useShouldRender';
import type { CoreProps } from '../../types';

export interface ImagePickerProps extends CoreProps {
  value?: string | null;
  onUpload: (file: File) => Promise<void>;
  /** Called when user removes the current image. */
  onClear?: () => void | Promise<void>;
  label?: string;
  size?: number;
  shape?: 'circle' | 'square';
  disabled?: boolean;
  clearable?: boolean;
}

function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return Boolean(cap?.isNativePlatform?.());
}

type CapacitorCameraModule = {
  Camera: {
    getPhoto: (opts: Record<string, unknown>) => Promise<{ dataUrl?: string }>;
  };
  CameraResultType: { DataUrl: string };
  CameraSource: { Camera: string; Photos: string };
};

/** Lazy-load only on native; Vite must not statically resolve this optional peer. */
async function loadCapacitorCamera(): Promise<CapacitorCameraModule | null> {
  try {
    const moduleId = '@capacitor/' + 'camera';
    return (await import(/* @vite-ignore */ moduleId)) as CapacitorCameraModule;
  } catch {
    return null;
  }
}

function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mime });
}

export function ImagePicker({
  value,
  onUpload,
  onClear,
  label = 'Image',
  size = 72,
  shape = 'circle',
  disabled = false,
  clearable = true,
  testId,
  view = 'all',
}: ImagePickerProps) {
  const shouldRender = useShouldRender(view);
  const inputRef = useRef<HTMLInputElement>(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Only true inside a real Capacitor shell — never on Vite web.
  const isNative = isCapacitorNative();
  const radius = shape === 'circle' ? '50%' : 'md';
  const hasImage = Boolean(value);
  const showClear = clearable && hasImage && Boolean(onClear) && !disabled;

  if (!shouldRender) return null;

  async function handleFile(file: File | null | undefined) {
    if (!file || disabled || uploading) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
    }
  }

  async function handleClear() {
    if (!onClear || disabled || clearing || uploading) return;
    setClearing(true);
    try {
      await onClear();
    } finally {
      setClearing(false);
    }
  }

  function openFilePicker() {
    inputRef.current?.click();
  }

  async function pickFromCamera() {
    setSourceOpen(false);
    const cameraMod = await loadCapacitorCamera();
    if (!cameraMod) {
      openFilePicker();
      return;
    }
    try {
      const { Camera, CameraResultType, CameraSource } = cameraMod;
      const photo = await Camera.getPhoto({
        quality: 85,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });
      if (photo.dataUrl) {
        await handleFile(dataUrlToFile(photo.dataUrl, `camera-${Date.now()}.jpg`));
      }
    } catch {
      openFilePicker();
    }
  }

  async function pickFromGallery() {
    setSourceOpen(false);
    const cameraMod = await loadCapacitorCamera();
    if (!cameraMod) {
      openFilePicker();
      return;
    }
    try {
      const { Camera, CameraResultType, CameraSource } = cameraMod;
      const photo = await Camera.getPhoto({
        quality: 85,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });
      if (photo.dataUrl) {
        await handleFile(dataUrlToFile(photo.dataUrl, `gallery-${Date.now()}.jpg`));
      }
    } catch {
      openFilePicker();
    }
  }

  function handlePress() {
    if (disabled || uploading) return;
    if (isNative) {
      setSourceOpen(true);
      return;
    }
    openFilePicker();
  }

  return (
    <>
      <Group gap="md" align="center" data-testid={testId} wrap="nowrap">
        <Box pos="relative" style={{ width: size, height: size, flexShrink: 0 }}>
          <UnstyledButton
            onClick={handlePress}
            disabled={disabled || uploading || clearing}
            style={{ display: 'block', width: size, height: size }}
            aria-label={label}
          >
            <Avatar
              src={value || undefined}
              size={size}
              radius={radius}
              color="brand"
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                background: '#fff',
              }}
            >
              {!value ? label.slice(0, 1).toUpperCase() : null}
            </Avatar>
            <ActionIcon
              variant="filled"
              color="brand"
              radius="xl"
              size="sm"
              pos="absolute"
              bottom={0}
              right={0}
              style={{ pointerEvents: 'none' }}
            >
              <IconCamera size={14} />
            </ActionIcon>
          </UnstyledButton>
        </Box>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600}>
            {label}
          </Text>
          <Text size="xs" c="dimmed">
            {uploading ? 'Uploading…' : clearing ? 'Removing…' : hasImage ? 'Tap to change' : 'Tap to add'}
          </Text>
          {showClear ? (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              loading={clearing}
              disabled={uploading}
              onClick={() => void handleClear()}
              style={{ alignSelf: 'flex-start', paddingInline: 0 }}
            >
              Remove image
            </Button>
          ) : null}
        </Stack>
      </Group>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          void handleFile(file);
          event.currentTarget.value = '';
        }}
      />

      <BottomDrawer opened={sourceOpen} onClose={() => setSourceOpen(false)} title="Choose source">
        <Stack gap="sm">
          <Button
            leftSection={<IconCamera size={18} />}
            variant="light"
            onClick={() => void pickFromCamera()}
          >
            Take photo
          </Button>
          <Button
            leftSection={<IconPhoto size={18} />}
            variant="light"
            onClick={() => void pickFromGallery()}
          >
            Choose from gallery
          </Button>
          {showClear ? (
            <Button
              leftSection={<IconTrash size={18} />}
              variant="light"
              color="red"
              loading={clearing}
              onClick={() => {
                setSourceOpen(false);
                void handleClear();
              }}
            >
              Remove image
            </Button>
          ) : null}
        </Stack>
      </BottomDrawer>
    </>
  );
}
